import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useGetExternalIngestionJobStatusLazyQuery,
  GetExternalIngestionJobStatusQuery,
} from "@axio/graphql/generated/graphql";

export type JobState = "waiting" | "active" | "completed" | "failed" | "delayed" | "paused";

export type JobStatusCategory = "pending" | "processing" | "completed" | "failed";

type ExternalIngestionJobFile = NonNullable<
  NonNullable<GetExternalIngestionJobStatusQuery["getExternalIngestionJobStatus"]["data"]>["file"]
>;

export interface JobStatus {
  jobId: string;
  state: JobState;
  category: JobStatusCategory;
  phase?: string | null;
  progress?: number | null;
  failedReason?: string | null;
  isTerminal: boolean;
  file?: ExternalIngestionJobFile | null;
}

export interface UseExternalIngestionStatusResult {
  jobStatuses: Map<string, JobStatus>;
  allComplete: boolean;
  anyFailed: boolean;
  isPolling: boolean;
  refetch: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function categorizeState(state: string): JobStatusCategory {
  switch (state) {
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    case "active":
      return "processing";
    case "waiting":
    case "delayed":
    case "paused":
    default:
      return "pending";
  }
}

function isTerminalState(state: string): boolean {
  return state === "completed" || state === "failed";
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 3000;

/**
 * Poll external ingestion job statuses for a list of job IDs.
 *
 * Automatically polls every 3000ms until all jobs reach a terminal state
 * (completed or failed). Stops polling when allComplete is true.
 *
 * @param jobIds - Array of job IDs to track. Can be updated dynamically.
 *
 * @example
 * ```tsx
 * const { jobStatuses, allComplete, anyFailed, isPolling } = useExternalIngestionStatus([
 *   'job-123',
 *   'job-456'
 * ]);
 *
 * // Check individual job status
 * const job123 = jobStatuses.get('job-123');
 * if (job123?.category === 'completed') {
 *   console.log('Job 123 done!');
 * }
 *
 * // Wait for all jobs to complete
 * if (allComplete) {
 *   console.log('All jobs finished');
 *   if (anyFailed) {
 *     console.log('Some jobs failed');
 *   }
 * }
 * ```
 */
export function useExternalIngestionStatus(jobIds: string[]): UseExternalIngestionStatusResult {
  const [fetchJobStatus] = useGetExternalIngestionJobStatusLazyQuery();
  const [jobStatuses, setJobStatuses] = useState<Map<string, JobStatus>>(new Map());
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize job IDs to avoid triggering effects on array reference changes
  const jobIdsKey = useMemo(() => jobIds.join(","), [jobIds]);

  // Poll all job IDs and update statuses
  const poll = useCallback(async () => {
    if (jobIds.length === 0) return;

    try {
      const results = await Promise.allSettled(
        jobIds.map((jobId) =>
          fetchJobStatus({
            variables: { jobId },
            fetchPolicy: "network-only",
          })
        )
      );

      // Build new status map
      const newStatuses = new Map<string, JobStatus | null>();

      results.forEach((result, index) => {
        const jobId = jobIds[index];

        if (result.status === "fulfilled" && result.value.data?.getExternalIngestionJobStatus.data) {
          const data = result.value.data.getExternalIngestionJobStatus.data;
          const state = data.state as JobState;
          newStatuses.set(jobId, {
            jobId: data.jobId,
            state,
            category: categorizeState(state),
            phase: data.phase,
            progress: data.progress,
            failedReason: data.failedReason,
            isTerminal: isTerminalState(state),
            file: data.file,
          });
        } else {
          newStatuses.set(jobId, null);
        }
      });

      setJobStatuses((prev) => {
        const merged = new Map<string, JobStatus>();
        newStatuses.forEach((status, jobId) => {
          if (status) {
            merged.set(jobId, status);
          } else {
            const existing = prev.get(jobId);
            if (existing) {
              merged.set(jobId, existing);
            } else {
              merged.set(jobId, {
                jobId,
                state: "waiting",
                category: "pending",
                phase: null,
                progress: null,
                failedReason: null,
                isTerminal: false,
                file: null,
              });
            }
          }
        });
        return merged;
      });
    } catch (error) {
      console.error("Error polling external ingestion job statuses:", error);
    }
  }, [jobIds, fetchJobStatus]);

  // Determine if all jobs are complete
  const allComplete = useMemo(() => {
    if (jobIds.length === 0) return true;
    if (jobStatuses.size !== jobIds.length) return false;
    return Array.from(jobStatuses.values()).every((status) => status.isTerminal);
  }, [jobIds.length, jobStatuses]);

  // Determine if any job failed
  const anyFailed = useMemo(() => {
    return Array.from(jobStatuses.values()).some((status) => status.category === "failed");
  }, [jobStatuses]);

  // Determine if currently polling
  const isPolling = useMemo(() => {
    return !allComplete && jobIds.length > 0;
  }, [allComplete, jobIds.length]);

  // Start/stop polling based on job list and completion status
  useEffect(() => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // If no jobs or all complete, don't poll
    if (jobIds.length === 0 || allComplete) {
      return;
    }

    // Initial poll
    poll();

    // Set up interval polling
    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [jobIdsKey, allComplete, poll]);

  // Force manual refetch
  const refetch = useCallback(async () => {
    await poll();
  }, [poll]);

  return {
    jobStatuses,
    allComplete,
    anyFailed,
    isPolling,
    refetch,
  };
}
